// Get API URL from runtime config or fallback to build-time env or default
const getApiBaseUrl = () => {
  // First, try to read from window.__ENV__ (injected at runtime)
  if (window.__ENV__ && window.__ENV__.REACT_APP_API_URL) {
    return window.__ENV__.REACT_APP_API_URL;
  }
  // Second, try to read from a config.js file (loaded in index.html)
  if (window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_URL) {
    return window.APP_CONFIG.REACT_APP_API_URL;
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:5050';
};

const API_BASE_URL = getApiBaseUrl();

// Get auth token
const getToken = () => {
  return localStorage.getItem('token');
};

// Portfolio Project API Service
export const portfolioProjectService = {
  // Get all portfolio projects (with filters)
  getAllPortfolioProjects: async (filters = {}) => {
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.year) queryParams.append('year', filters.year);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.featured !== undefined) queryParams.append('featured', filters.featured);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const url = `${API_BASE_URL}/api/portfolio-projects${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch portfolio projects');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get portfolio project by ID
  getPortfolioProjectById: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/portfolio-projects/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch portfolio project');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Create portfolio project (with file upload)
  createPortfolioProject: async (formData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/portfolio-projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to create portfolio project');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update portfolio project (with optional file upload)
  updatePortfolioProject: async (id, formData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/portfolio-projects/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to update portfolio project');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete portfolio project
  deletePortfolioProject: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/portfolio-projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete portfolio project');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Submit portfolio project via webhook (public endpoint - no auth required)
  submitPortfolioProject: async (projectData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/portfolio-projects/webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to submit portfolio project');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

